import { hasLowercase, hasNumber, hasUppercase } from '@/shared/utils';
import { FIELD_MAX_LENGTHS } from '@/shared/constants';
import { Button, Field, Text } from '@noorden-insurtech/alba-design-system';
import { Check, Eye, EyeClosed, X } from 'lucide-react';
import React, { useMemo, useState } from 'react';
import { ErrorMessage } from './error-message.component';

interface ResetPasswordFormProps {
  showCode: boolean;
  /** Token na URL (fluxo admin com link) - não exibe campo de código */
  tokenFromUrl?: string;
  onSubmit: (
    password: string,
    confirmPassword: string,
    codeOrToken: string
  ) => Promise<void>;
  loading?: boolean;
  error?: string;
}

export function ResetPasswordForm({
  showCode,
  tokenFromUrl,
  onSubmit,
  error,
}: ResetPasswordFormProps) {
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [code, setCode] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  const requirements = useMemo(() => {
    return {
      minLength: password.length >= 12,
      number: hasNumber(password),
      uppercase: hasUppercase(password),
      lowercase: hasLowercase(password),
      specialChar: /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(password),
    };
  }, [password]);

  const codeValid = showCode
    ? code.length === 6 && /^\d{6}$/.test(code)
    : true;
  const tokenValid = tokenFromUrl ? !!tokenFromUrl : true;
  const allValid =
    requirements.minLength &&
    requirements.number &&
    requirements.uppercase &&
    requirements.lowercase &&
    requirements.specialChar &&
    password === confirmPassword &&
    codeValid &&
    tokenValid;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!allValid) return;
    const codeOrToken = tokenFromUrl ?? code;
    await onSubmit(password, confirmPassword, codeOrToken);
  };

  return (
    <form onSubmit={handleSubmit} className='flex flex-col gap-6 w-full'>
      {showCode && (
        <Field.Root>
          <Field.Label>Código</Field.Label>
          <div className='relative'>
            <Field.Input
              type={'text'}
              value={code}
              onChange={e => {
                const value = e.target.value.replace(/\D/g, '').slice(0, 6);
                setCode(value);
              }}
              placeholder='Digite o código de 6 dígitos'
              className='w-full'
              required
              maxLength={6}
            />
          </div>
          {code && code.length !== 6 && (
            <Text size='xs' className='text-[#c54d4d] mt-1'>
              O código deve ter exatamente 6 dígitos
            </Text>
          )}
        </Field.Root>
      )}
      <Field.Root>
        <Field.Label>Nova Senha</Field.Label>
        <div className='relative'>
          <Field.Input
            type={showPassword ? 'text' : 'password'}
            value={password}
            onChange={e => setPassword(e.target.value)}
            placeholder=''
            className='w-full'
            required
            maxLength={FIELD_MAX_LENGTHS.password}
          />
          <button
            type='button'
            onClick={() => setShowPassword(!showPassword)}
            className='absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#9fa4a6] hover:text-[#2c2864] transition-colors'
            aria-label={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
            title={showPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showPassword ? <Eye size={20} /> : <EyeClosed size={20} />}
          </button>
        </div>
      </Field.Root>

      <div className='bg-[#EFF0F1] border border-[#E6E8EA] rounded-[10px] px-4 py-3'>
        <div className='mb-2'>
          <Text size='xs' className='text-[#2c2864] font-semibold'>
            Requisitos da senha:
          </Text>
        </div>
        <ul className='space-y-1'>
          <li className='flex items-center gap-2 text-sm'>
            {requirements.minLength ? (
              <Check size={16} color='#129440' />
            ) : (
              <X size={16} color='#777B7C' />
            )}
            <span
              className={
                requirements.minLength ? 'text-[#129440]' : 'text-[#2c2864]'
              }
            >
              mínimo de 12 caracteres
            </span>
          </li>
          <li className='flex items-center gap-2 text-sm'>
            {requirements.number ? (
              <Check size={16} color='#129440' />
            ) : (
              <X size={16} color='#777B7C' />
            )}
            <span
              className={
                requirements.number ? 'text-[#129440]' : 'text-[#2c2864]'
              }
            >
              números
            </span>
          </li>
          <li className='flex items-center gap-2 text-sm'>
            {requirements.uppercase ? (
              <Check size={16} color='#129440' />
            ) : (
              <X size={16} color='#777B7C' />
            )}
            <span
              className={
                requirements.uppercase ? 'text-[#129440]' : 'text-[#2c2864]'
              }
            >
              letras maiúsculas
            </span>
          </li>
          <li className='flex items-center gap-2 text-sm'>
            {requirements.lowercase ? (
              <Check size={16} color='#129440' />
            ) : (
              <X size={16} color='#777B7C' />
            )}
            <span
              className={
                requirements.lowercase ? 'text-[#129440]' : 'text-[#2c2864]'
              }
            >
              letras minúsculas
            </span>
          </li>
          <li className='flex items-center gap-2 text-sm'>
            {requirements.specialChar ? (
              <Check size={16} color='#129440' />
            ) : (
              <X size={16} color='#777B7C' />
            )}
            <span
              className={
                requirements.specialChar ? 'text-[#129440]' : 'text-[#2c2864]'
              }
            >
              caracteres especiais (!@#$%^&*...)
            </span>
          </li>
        </ul>
      </div>

      <Field.Root>
        <Field.Label>Confirmar Senha</Field.Label>
        <div className='relative'>
          <Field.Input
            type={showConfirmPassword ? 'text' : 'password'}
            value={confirmPassword}
            onChange={e => setConfirmPassword(e.target.value)}
            placeholder=''
            className='w-full'
            required
            maxLength={FIELD_MAX_LENGTHS.password}
          />
          <button
            type='button'
            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
            className='absolute right-4 top-1/2 transform -translate-y-1/2 w-6 h-6 flex items-center justify-center text-[#9fa4a6] hover:text-[#2c2864] transition-colors'
            aria-label={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
            title={showConfirmPassword ? 'Ocultar senha' : 'Mostrar senha'}
          >
            {showConfirmPassword ? <Eye size={20} /> : <EyeClosed size={20} />}
          </button>
        </div>
        {confirmPassword && confirmPassword !== password && (
          <Text size='xs' className='text-[#c54d4d] mt-1'>
            As senhas não coincidem
          </Text>
        )}
      </Field.Root>

      {error && <ErrorMessage message={error} />}

      <Button
        type='submit'
        variant='primary'
        size='large'
        disabled={!allValid}
        className='w-full'
      >
        {'Confirmar'}
      </Button>
    </form>
  );
}
